import { Controller } from "@hotwired/stimulus"

// Searchable checkboxes grouped under parent checkboxes.
//
// A parent selects or clears its whole group, including items hidden by the search.
// Parents are server-disabled and nameless so they never submit; connect() enables them.
// A search term matches an item's text or its group's, so naming a group shows all of it.
export default class extends Controller {
  static targets = ["count", "empty", "group", "input", "selectedOnly"]

  #groups = []

  connect() {
    this.#groups = this.groupTargets.map((element) => {
      const groupText = element.dataset.groupedCheckboxPickerText.toLowerCase()
      return {
        badge: element.querySelector("[data-total-label]"),
        element,
        items: Array.from(element.querySelectorAll("[data-grouped-checkbox-picker-text]"), (row) => ({
          checkbox: row.querySelector("input[type=checkbox]"),
          row,
          text: `${groupText} ${row.dataset.groupedCheckboxPickerText.toLowerCase()}`,
        })),
        parent: element.querySelector("[data-parent]"),
      }
    })
    this.#groups.forEach((group) => {
      group.parent.disabled = false
      this.#refreshGroup(group)
    })
    this.#refreshCount()
  }

  filter() {
    const terms = this.inputTarget.value.toLowerCase().split(/\s+/).filter(Boolean)
    const selectedOnly = this.selectedOnlyTarget.checked
    let matchedGroups = 0

    this.#groups.forEach((group) => {
      let matchedItems = 0
      group.items.forEach(({ checkbox, row, text }) => {
        const hit = terms.every((term) => text.includes(term)) && (!selectedOnly || checkbox.checked)
        row.classList.toggle("hidden", !hit)
        if (hit) matchedItems++
      })
      group.element.classList.toggle("hidden", matchedItems === 0)
      if (matchedItems > 0) matchedGroups++
    })

    this.emptyTarget.classList.toggle("hidden", matchedGroups > 0)
  }

  refresh(event) {
    this.#refreshGroup(this.#groupFor(event.target))
    this.#refreshCount()
  }

  toggleParent(event) {
    const group = this.#groupFor(event.target)
    group.items.forEach(({ checkbox }) => {
      checkbox.checked = event.target.checked
    })
    this.#refreshGroup(group)
    this.#refreshCount()
  }

  #checkedCount(group) {
    return group.items.filter(({ checkbox }) => checkbox.checked).length
  }

  #groupFor(element) {
    return this.#groups.find((group) => group.element.contains(element))
  }

  #refreshCount() {
    this.countTarget.textContent = this.#groups.reduce((sum, group) => sum + this.#checkedCount(group), 0)
  }

  #refreshGroup(group) {
    const checked = this.#checkedCount(group)
    const total = group.items.length

    group.parent.checked = checked > 0 && checked === total
    group.parent.indeterminate = checked > 0 && checked < total
    group.badge.textContent = checked > 0 ? `${checked} of ${total} selected` : group.badge.dataset.totalLabel
    group.badge.classList.toggle("bg-blue-50", checked > 0)
    group.badge.classList.toggle("text-blue-700", checked > 0)
    group.badge.classList.toggle("text-gray-400", checked === 0)
  }
}
